/**
 * Uniform error shape for the API client. Real transport should translate
 * HTTP error responses into this same shape so every view can handle errors
 * one way regardless of adapter (mock today, HTTP/WS later).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

export function isNotFound(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 404;
}

/**
 * True for a licensing refusal (ADR-0017 §2.3): a NEW action was refused
 * because a licensed limit or a plan-gated feature was reached, never
 * because anything already running was touched (charter §1's safety
 * corollary). Every 402 the server emits carries its own plain-language
 * `message` explaining the limit and how to raise it — callers should
 * render `error.message` verbatim rather than a generic failure, and link
 * to the plan/billing page rather than inventing their own copy.
 */
export function isPaymentRequired(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 402;
}

/**
 * True for a capability that is genuinely not built yet (e.g. `deployService`
 * — `TriggerDeployHandler` answers 501 unconditionally, server/internal/
 * projectsvc/http.go). Callers MUST render the server's own message as a
 * persistent, honest "not available yet" state — never retry silently, never
 * treat it as a generic failure, and never assume success happened anyway.
 */
export function isNotImplemented(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 501;
}
